'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { AlertDialog } from '@/components/common/AlertDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { articlesApi } from '@/app/api/articles';
import { useAuth } from '@/context/AuthContext';

import ArticleEditor from '../components/ArticleEditor';
import { ArticleWizardModal, ArticlePlanningData, AIGeneratedArticle } from './components';
import { ArticleMeta, ArticleMetadata } from '../utils/article-meta';
import { generateMarkdownContent } from '../utils/content-parser';

export default function CreateArticlePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(() => searchParams.get('start') !== 'blank');

  // Get imported content from sessionStorage (for new tab imports) or navigation state (for same tab)
  // sessionStorage will be automatically cleared when the tab/browser closes
  const importId = searchParams.get('import');
  const [importData] = useState(() => {
    if (importId) {
      // Load from sessionStorage (new tab import)
      const storedData = sessionStorage.getItem(importId);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          console.log('[Create Page] Loaded imported content:', {
            title: parsed.importedTitle,
            contentLength: parsed.importedContent?.length,
            filename: parsed.importedFilename
          });
          // Don't remove - let browser clean up when tab closes
          return {
            importedTitle: parsed.importedTitle || '',
            importedContent: parsed.importedContent || '',
            isImported: parsed.isImported || false
          };
        } catch (e) {
          console.error('Failed to parse imported content:', e);
        }
      } else {
        console.warn('[Create Page] No data found in sessionStorage for importId:', importId);
      }
    } else {
      // Fallback to navigation state (same tab import - legacy)
      const locationState = (window.history.state && window.history.state.usr) || {};
      if (locationState.isImported) {
        console.log('[Create Page] Using navigation state for import');
        return {
          importedTitle: locationState.importedTitle || '',
          importedContent: locationState.importedContent || '',
          isImported: locationState.isImported || false
        };
      }
    }

    return {
      importedTitle: '',
      importedContent: '',
      isImported: false
    };
  });

  const { importedTitle, importedContent, isImported } = importData;

  // Alert dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Error');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => { });
  const [isConfirmDialog, setIsConfirmDialog] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState<AIGeneratedArticle | null>(null);

  // State to store wizard form data
  const [wizardFormData, setWizardFormData] = useState<ArticlePlanningData>({
    purpose: '',
    targetAudience: 'potential',
    intent: 'growth',
    desiredLength: 'medium' as 'short' | 'medium' | 'long',
    tone: 'professional',
    tags: [] as string[],
    category: ''
  });

  const handleSave = async (articleData: {
    title: string;
    content: string;
    status: string;
    category: string;
    tags: string[];
    meta?: {
      suggested_titles?: string[];
      [key: string]: any;
    };
  }) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!articleData.title) {
        throw new Error('Title is required');
      }

      if (!articleData.content) {
        throw new Error('Content is required');
      }

      // Prepare metadata for the new article
      const metadata = {
        title: articleData.title,
        category: articleData.category,
        tags: articleData.tags,
        status: articleData.status,
        aiContext: {
          purpose: wizardFormData.purpose || '',
          targetAudience: wizardFormData.targetAudience || '',
          intent: wizardFormData.intent || '',
          desiredLength: wizardFormData.desiredLength || 'medium',
          tone: wizardFormData.tone || 'professional',
          tags: wizardFormData.tags || [],
          suggestedTags: generatedArticle?.tags || [],
          category: articleData.category
        }
      };

      // 1. Create the article with metadata
      const newArticle = await articlesApi.createArticle(metadata);

      // 2. Update the article content
      await articlesApi.updateArticleContent(newArticle.uuid, articleData.content);

      // 3. Set the tags for the article
      const allTags = [...new Set([...articleData.tags, ...(generatedArticle?.tags || [])])];
      if (allTags.length > 0) {
        await articlesApi.setArticleTags(newArticle.uuid, allTags);
      }

      // 4. Navigate back to the articles list
      navigate('/articles');
    } catch (error: any) {
      setAlertTitle('Error Creating Article');
      setAlertMessage(`Failed to create article: ${error.message || 'Unknown error'}. Please try again.`);
      setAlertDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setAlertTitle('Confirm Cancellation');
    setAlertMessage('Are you sure you want to cancel? Any unsaved changes will be lost.');
    setIsConfirmDialog(true);
    setConfirmAction(() => () => navigate('/articles'));
    setAlertDialogOpen(true);
  };

  // Save form data and close wizard when user explicitly closes it
  const handleWizardClose = () => {
    setIsWizardOpen(false);
  };

  // Handle when the wizard is completed with a generated article
  const handleWizardComplete = (article: AIGeneratedArticle, formData: ArticlePlanningData) => {
    // Create a copy of the article to avoid reference issues
    const articleCopy = { ...article };

    // Ensure the article has a selectedTitle set
    if (articleCopy && articleCopy.titles && articleCopy.titles.length > 0) {
      // If no selectedTitle is set or it's empty, use the first title
      if (!articleCopy.selectedTitle || articleCopy.selectedTitle.trim() === '') {
        articleCopy.selectedTitle = articleCopy.titles[0];
      }
    }

    // Make sure titles is an array
    if (!articleCopy.titles) {
      articleCopy.titles = [];
    }

    setGeneratedArticle(articleCopy);
    setWizardFormData(formData); // Save the form data when wizard completes
    setIsWizardOpen(false);
  };

  // Prepare suggested titles to pass to ArticleEditor
  const suggestedTitlesToPass = generatedArticle?.titles && Array.isArray(generatedArticle.titles)
    ? [...generatedArticle.titles]
    : [];

  // Debug: Log what we're passing to ArticleEditor
  console.log('[Create Page] Passing to ArticleEditor:', {
    isImported,
    importedTitle,
    importedContentLength: importedContent.length,
    generatedTitle: generatedArticle?.selectedTitle,
    finalTitle: isImported ? importedTitle : (generatedArticle?.selectedTitle || ''),
    finalContentLength: isImported ? importedContent.length : (generatedArticle ? generateMarkdownContent(generatedArticle).length : 0)
  });

  return (
    <>
      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={alertDialogOpen}
        onClose={() => setAlertDialogOpen(false)}
        title={alertTitle}
        message={alertMessage}
        isConfirmation={isConfirmDialog}
        onConfirm={confirmAction}
        confirmLabel={isConfirmDialog ? 'Yes' : 'OK'}
        cancelLabel="No"
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ArticleEditor
            key={isImported ? 'imported' : 'generated'}
            mode="create"
            onSave={handleSave}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            initialTitle={isImported ? importedTitle : (generatedArticle?.selectedTitle || '')}
            initialContent={isImported ? importedContent : (generatedArticle ? generateMarkdownContent(generatedArticle) : '')}
            initialStatus="draft"
            initialCategory={wizardFormData.category || ''}
            initialTags={generatedArticle?.tags || []}
            initialSuggestedTitles={suggestedTitlesToPass}
            onWizardOpen={() => setIsWizardOpen(true)}
          />

          {isWizardOpen && (
            <ArticleWizardModal
              isOpen={isWizardOpen}
              onClose={handleWizardClose}
              onComplete={handleWizardComplete}
              initialFormData={wizardFormData}
              onFormDataChange={setWizardFormData}
              showCreateFromBlank={false}
            />
          )}
        </div>
      </div>
    </>
  );
}

// Function moved to /src/app/articles/utils/content-parser.ts

